/* ===== Projects Page ===== */

import { useEffect, useState } from 'react'
import type { Project, ProjectStatus } from '@zetyar/domain'
import { Briefcase, Plus, Trash2 } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webProjectRepository } from '@/infrastructure/repositories/projects'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

const STATUS_LABEL: Record<ProjectStatus, string> = {
  draft: 'Borrador',
  planned: 'Planificado',
  active: 'Activo',
  paused: 'Pausado',
  completed: 'Completado',
  cancelled: 'Cancelado',
}

const STATUS_OPTIONS: ProjectStatus[] = ['planned', 'active', 'paused', 'completed', 'cancelled']

const STATUS_BADGE_VARIANT: Record<ProjectStatus, 'primary' | 'neutral' | 'success' | 'warning' | 'danger'> = {
  draft: 'neutral',
  planned: 'neutral',
  active: 'primary',
  paused: 'warning',
  completed: 'success',
  cancelled: 'danger',
}

export function ProjectsPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const { guard, reauthModal } = useReauthGuard()
  const canCreate = profile?.isSuperAdmin || permissions.includes('projects.create')
  const canManageProjects = profile?.isSuperAdmin || permissions.includes('projects.manage')
  const canDelete = canManageProjects
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const loadProjects = async () => {
    const context = tenantContext()
    if (!context) {
      setProjects([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const result = await webProjectRepository.listVisible(context, { limit: 100 })
      setProjects(result)
    } catch (error) {
      console.error('Error al cargar proyectos:', error)
      setProjects([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      if (!isMounted) return
      await loadProjects()
    }

    void run()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context) return

    try {
      if (webProjectRepository.create) {
        await webProjectRepository.create(context, { title, description: description || undefined })
        await loadProjects()
      }
    } catch (error) {
      console.error('Error al crear el proyecto en Supabase:', error)
    }

    setShowCreateModal(false)
    setTitle('')
    setDescription('')
  }

  const handleStatusChange = async (project: Project, status: ProjectStatus) => {
    const context = tenantContext()
    if (!context || !webProjectRepository.update) return

    try {
      await webProjectRepository.update(context, project.id, { status })
      setProjects((current) =>
        current.map((p) => (p.id === project.id ? { ...p, status } : p)),
      )
    } catch (error) {
      console.error('Error al actualizar el estado del proyecto:', error)
    }
  }

  const handleDelete = (projectId: string) => {
    guard(async () => {
      const context = tenantContext()
      if (!context || !webProjectRepository.delete) return

      try {
        await webProjectRepository.delete(context, projectId)
        setProjects((current) => current.filter((item) => item.id !== projectId))
      } catch (error) {
        console.error('Error al eliminar el proyecto:', error)
      }
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Proyectos</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Seguimiento de iniciativas y metas de {currentOrganization?.name}
          </p>
        </div>
        {canCreate && (
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            Crear proyecto
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary-600" />
            Proyectos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">
              Cargando proyectos desde Supabase...
            </p>
          ) : projects.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              No hay proyectos cargados en {currentOrganization?.name}.
            </p>
          ) : (
            <div className="space-y-4">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="p-4 rounded-md border border-neutral-200 dark:border-neutral-800 space-y-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">{project.title}</h3>
                      {project.description && (
                        <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-1">{project.description}</p>
                      )}
                    </div>
                    <Badge variant={STATUS_BADGE_VARIANT[project.status]}>{STATUS_LABEL[project.status]}</Badge>
                  </div>

                  <div className="w-full h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                    <div
                      className="h-full bg-primary-600"
                      style={{ width: `${Math.max(0, Math.min(100, project.progress))}%` }}
                    />
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-500 dark:text-neutral-400">
                    <span>{project.progress}% completado</span>
                    <div className="flex items-center gap-2">
                      {canManageProjects ? (
                        <select
                          value={project.status}
                          onChange={(e) => void handleStatusChange(project, e.target.value as ProjectStatus)}
                          className="h-8 px-2 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        >
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {STATUS_LABEL[status]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Badge variant="neutral">{STATUS_LABEL[project.status]}</Badge>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0 flex items-center justify-center"
                          aria-label="Eliminar proyecto"
                          onClick={() => handleDelete(project.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-danger-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Crear proyecto">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Titulo del proyecto"
            placeholder="Ej: Renovacion de sede"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Descripcion (opcional)</label>
            <textarea
              className="w-full h-24 p-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              placeholder="Objetivo y alcance del proyecto..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Crear proyecto
            </Button>
          </div>
        </form>
      </Modal>
      {reauthModal}
    </div>
  )
}
